import { Component, Output, EventEmitter, Input, Inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CoreService } from 'src/app/services/core.service';
import { MatDialog } from '@angular/material/dialog';
import { Router, RouterModule } from '@angular/router';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MaterialModule } from 'src/app/material.module';
import { BrandingComponent } from '../../vertical/sidebar/branding.component';
import { AppSettings } from 'src/app/config';
import { AuthenticationService } from 'src/app/services/apps/Authentication/authentication.service';
import { logoutToLogin } from 'src/app/services/apps/Authentication/logout-to-login';
import { NotificationsService, NotificationItem, NotificationKind, formatNotificationDescription } from 'src/app/services/notifications.service';
import { InvoicesService } from 'src/app/services/apps/invoices/invoices.service';
import { ManualInvoiceDialogComponent } from 'src/app/pages/apps/invoices/manual-invoice-dialog.component';
import { interval, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { NgScrollbarModule } from 'ngx-scrollbar';

// Removed old mock interface

@Component({
  selector: 'app-horizontal-header',
  imports: [
    CommonModule,
    RouterModule,
    TablerIconsModule,
    MaterialModule,
    BrandingComponent,
    NgScrollbarModule,
  ],
  templateUrl: './header.component.html',
  styles: [`
    .notification-badge {
      position: absolute;
      top: 0px;
      right: 0px;
      color: white;
      border-radius: 50%;
      padding: 0;
      font-size: 10px;
      font-weight: 700;
      line-height: 1;
      min-width: 18px;
      width: 18px;
      height: 18px;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      border: 2px solid white;
      z-index: 10;
    }
    .notifications-scroll {
      max-height: 400px;
      overflow-x: hidden;
      overflow-y: auto;
    }
    .notification-row {
      min-width: 0;
      gap: 8px;
    }
    .notification-body {
      min-width: 0;
      flex: 1 1 auto;
    }
    .notification-text {
      word-break: break-word;
      overflow-wrap: anywhere;
      line-height: 1.35;
    }
  `],
})
export class AppHorizontalHeaderComponent implements OnInit, OnDestroy {
  @Input() showToggle = true;
  @Input() toggleChecked = false;
  @Output() toggleMobileNav = new EventEmitter<void>();
  @Output() toggleMobileFilterNav = new EventEmitter<void>();
  @Output() toggleCollapsed = new EventEmitter<void>();

  isCollapse: boolean = false; // Initially hidden

  toggleCollpase() {
    this.isCollapse = !this.isCollapse; // Toggle visibility
  }

  showFiller = false;

  @Output() optionsChange = new EventEmitter<AppSettings>();
  
  notifications: NotificationItem[] = [];
  private pollingSubscription?: Subscription;
  
  // Exponer enum para usarlo en el template
  NotificationKind = NotificationKind;
  formatNotificationDescription = formatNotificationDescription;

  userData: any = null;
  displayName = 'User';
  private currentUserSubscription?: Subscription;

  constructor(
    private settings: CoreService,
    private vsidenav: CoreService,
    private dialog: MatDialog,
    private router: Router,
    @Inject(AuthenticationService)private authenticationService: AuthenticationService,
    private notificationsService: NotificationsService,
    private invoicesService: InvoicesService
  ) {
    this.updateUserDisplay(this.authenticationService.getCurrentUserSnapshot());
    this.currentUserSubscription = this.authenticationService.currentUser$.subscribe(
      (user) => this.updateUserDisplay(user)
    );
  }

  private updateUserDisplay(user: any | null): void {
    this.userData = user;
    this.displayName = this.authenticationService.getDisplayName(user);
  }
  onLogout(): void {
    logoutToLogin(this.authenticationService, this.router);
  }
  options = this.settings.getOptions();

  private emitOptions() {
    this.optionsChange.emit(this.options);
  }

  setlightDark(theme: string) {
    this.options.theme = theme;
    this.emitOptions();
  }

  ngOnInit(): void {
    // Cargar notificaciones inmediatamente
    this.loadNotifications();

    // Configurar polling cada 60 segundos
    this.pollingSubscription = interval(60000)
      .pipe(switchMap(() => this.notificationsService.getUnreadNotifications()))
      .subscribe({
        next: (response) => {
          if (response.success && response.requestResult) {
            this.notifications = response.requestResult;
          }
        },
        error: (error) => {
          console.error('Error polling notifications:', error);
        }
      });
  }

  ngOnDestroy(): void {
    // Limpiar subscription del polling
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }
    if (this.currentUserSubscription) {
      this.currentUserSubscription.unsubscribe();
    }
  }

  loadNotifications(): void {
    this.notificationsService.getUnreadNotifications().subscribe({
      next: (response) => {
        console.log('[Notifications] Response received:', response);
        if (response.success && response.requestResult) {
          this.notifications = response.requestResult;
          console.log('[Notifications] Loaded count:', this.notifications.length);
          console.log('[Notifications] Notifications array:', this.notifications);
        } else {
          console.warn('[Notifications] No valid data in response');
          this.notifications = [];
        }
      },
      error: (error) => {
        console.error('[Notifications] Error loading notifications:', error);
        this.notifications = []; // Asegurar que esté vacío en caso de error
      }
    });
  }

  markNotificationAsRead(notificationId: string): void {
    this.notificationsService.markAsRead(notificationId).subscribe({
      next: (response) => {
        if (response.success) {
          // Recargar notificaciones después de marcar como leída
          this.loadNotifications();
        }
      },
      error: (error) => {
        console.error('Error marking notification as read:', error);
      }
    });
  }

  onNotificationClick(notification: NotificationItem): void {
    // Si es una notificación de factura generada, abrir el modal de factura
    if (notification.kind === 1 && notification.externalId) { // NotificationKind.InvoiceGenerated
      // Obtener los detalles de la factura
      this.invoicesService.getInvoiceById(notification.externalId).subscribe({
        next: (response) => {
          if (response.success && response.body) {
            const invoice = response.body;
            
            // Abrir el modal de factura en modo "view"
            const dialogRef = this.dialog.open(ManualInvoiceDialogComponent, {
              width: '900px',
              maxWidth: '95vw',
              maxHeight: '90vh',
              disableClose: true,
              panelClass: 'invoice-dialog',
              data: { 
                mode: 'view',
                invoice: invoice,
                showCancelButton: invoice.type === 'Cash' && invoice.status === 'Paid',
                reverseMode: true
              }
            });
            
            // Marcar como leída al abrir el modal
            this.markNotificationAsRead(notification.id);
          }
        },
        error: (error) => {
          console.error('Error loading invoice:', error);
        }
      });
    }
  }

  trackByNotificationId(index: number, notification: NotificationItem): string {
    return notification.id;
  }
}
